import React, { useEffect, useState, useMemo } from 'react';
import './InventoryDashboard.css';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Input,
  Space,
  Tag,
  App,
  Card,
  Select,
  Row,
  Col,
  Statistic,
  Badge,
  Progress,
  Typography,
  Alert
} from 'antd';
import {
  SearchOutlined,
  WarningOutlined,
  DashboardOutlined,
  InboxOutlined,
  ExclamationCircleOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currency';
import { Inventory, InventoryBatch, InventoryMovement, ProductVariant } from '../types/pz';
import { canManageInventory } from '../modules/inventory/inventoryAccessPolicy';
import { fetchBatchEarmarks, BatchEarmark } from '../modules/orders/api/orderEarmarksApi';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';

const { Option } = Select;
const { Text } = Typography;

interface InventoryWithDetails extends Inventory {
  productVariant?: ProductVariant & {
    product?: {
      id: string;
      code: string;
      name: string;
      category?: string;
    };
  };
}

interface InventoryFilters {
  search: string;
  status: string | null;
  lowStock: boolean;
}

interface InventorySummary {
  totalItems: number;
  totalValue: number;
  lowStockCount: number;
  outOfStockCount: number;
}

const InventoryDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['inventory', 'common']);
  const { message } = App.useApp();

  const [inventory, setInventory] = useState<InventoryWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<InventoryFilters>({
    search: '',
    status: null,
    lowStock: false,
  });
  const [summary, setSummary] = useState<InventorySummary>({
    totalItems: 0,
    totalValue: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [batches, setBatches] = useState<Record<string, InventoryBatch[]>>({});
  const [movements, setMovements] = useState<Record<string, InventoryMovement[]>>({});
  // batchEarmarks: keyed by batchId (batch.id), loaded on demand per batch row
  const [batchEarmarks, setBatchEarmarks] = useState<Record<string, BatchEarmark[]>>({});

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const canEdit = canManageInventory(authService.getCurrentUser());
  const canViewPurchasePrices = authService.canViewSupplierPurchasePrices();

  useEffect(() => {
    fetchInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const response = await api.get('/inventory?includeVariant=true');
      const inventoryData = response.data;
      setInventory(inventoryData);

      // Calculate summary
      const totalValue = canViewPurchasePrices
        ? inventoryData.reduce((sum: number, inv: InventoryWithDetails) => sum + (inv.totalValuePLN || 0), 0)
        : 0;
      const lowStockCount = inventoryData.filter((inv: InventoryWithDetails) =>
        inv.quantityAvailable > 0 && inv.quantityAvailable < 10).length;
      const outOfStockCount = inventoryData.filter((inv: InventoryWithDetails) =>
        inv.quantityAvailable <= 0).length;

      setSummary({
        totalItems: inventoryData.length,
        totalValue,
        lowStockCount,
        outOfStockCount,
      });
    } catch (error: any) {
      message.error(error.response?.data?.error || t('inventory:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  usePageAutoRefresh(fetchInventory);

  const fetchBatches = async (variantId: string) => {
    try {
      const response = await api.get(`/inventory/variant/${variantId}/batches`);
      setBatches(prev => ({ ...prev, [variantId]: response.data }));
    } catch (error: any) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchMovements = async (variantId: string) => {
    try {
      const response = await api.get(`/inventory/variant/${variantId}/movements?limit=10`);
      setMovements(prev => ({ ...prev, [variantId]: response.data }));
    } catch (error: any) {
      console.error('Error fetching movements:', error);
    }
  };

  const fetchEarmarksForBatch = async (batchId: string) => {
    if (batchEarmarks[batchId] !== undefined) return; // already loaded
    try {
      const earmarks = await fetchBatchEarmarks(batchId);
      setBatchEarmarks(prev => ({ ...prev, [batchId]: earmarks }));
    } catch (error: any) {
      console.error('Error fetching batch earmarks:', error);
      setBatchEarmarks(prev => ({ ...prev, [batchId]: [] }));
    }
  };

  const handleExpand = (expanded: boolean, record: InventoryWithDetails) => {
    if (expanded) {
      const variantId = record.productVariantId;
      setExpandedRowKeys([...expandedRowKeys, variantId]);
      if (!batches[variantId]) {
        fetchBatches(variantId);
      }
      if (!movements[variantId]) {
        fetchMovements(variantId);
      }
    } else {
      setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.productVariantId));
    }
  };

  const filteredInventory = useMemo(() => {
    return inventory.filter(inv => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesVariant =
          inv.productVariant?.sku.toLowerCase().includes(searchLower) ||
          inv.productVariant?.variantName.toLowerCase().includes(searchLower);
        const matchesProduct =
          inv.productVariant?.product?.code.toLowerCase().includes(searchLower) ||
          inv.productVariant?.product?.name.toLowerCase().includes(searchLower);
        if (!matchesVariant && !matchesProduct) return false;
      }

      // Status filter
      if (filters.status && inv.status !== filters.status) return false;

      // Low stock filter
      if (filters.lowStock && inv.quantityAvailable >= 10) return false;

      return true;
    });
  }, [inventory, filters]);

  const getStockStatus = (inv: InventoryWithDetails) => {
    const available = inv.quantityAvailable || 0;
    const minLevel = inv.productVariant?.minStockLevel || 10;

    if (available <= 0) {
      return { status: 'error' as const, text: t('inventory:status.outOfStock'), color: 'red' };
    }
    if (available < minLevel) {
      return { status: 'warning' as const, text: t('inventory:status.lowStock'), color: 'orange' };
    }
    return { status: 'success' as const, text: t('inventory:status.inStock'), color: 'green' };
  };

  const getMovementTypeTag = (type: string) => {
    switch (type) {
      case 'PZ_IN':
        return <Tag color="green">PZ</Tag>;
      case 'WZ_OUT':
        return <Tag color="red">WZ</Tag>;
      case 'ADJUSTMENT':
      case 'ADJUSTMENT_IN':
      case 'ADJUSTMENT_OUT':
        return <Tag color="blue">{t('inventory:movementType.adjustment')}</Tag>;
      case 'TRANSFER':
        return <Tag color="purple">{t('inventory:movementType.transfer')}</Tag>;
      default:
        return <Tag>{type}</Tag>;
    }
  };

  const columns = [
    {
      title: t('inventory:fields.sku'),
      key: 'sku',
      width: 150,
      render: (record: InventoryWithDetails) => (
        <Text strong>{record.productVariant?.sku}</Text>
      ),
    },
    {
      title: t('inventory:fields.productName'),
      key: 'productName',
      render: (record: InventoryWithDetails) => (
        <div>
          <div>{record.productVariant?.variantName}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.productVariant?.product?.name}
          </Text>
        </div>
      ),
    },
    {
      title: t('inventory:fields.quantityAvailable'),
      dataIndex: 'quantityAvailable',
      key: 'quantityAvailable',
      width: 120,
      render: (qty: number, record: InventoryWithDetails) => {
        const { status, color } = getStockStatus(record);
        return (
          <Badge status={status} text={<Text style={{ color }}>{qty}</Text>} />
        );
      },
    },
    {
      title: t('inventory:fields.quantityReserved'),
      dataIndex: 'quantityReserved',
      key: 'quantityReserved',
      width: 120,
      render: (qty: number) => qty || 0,
    },
    {
      title: t('inventory:fields.quantityInTransit'),
      dataIndex: 'quantityInTransit',
      key: 'quantityInTransit',
      width: 120,
      render: (qty: number) => qty || 0,
    },
    ...(canViewPurchasePrices ? [{
      title: t('inventory:fields.averageCost'),
      dataIndex: 'averageCostPLN',
      key: 'averageCostPLN',
      width: 120,
      render: (cost: number) => cost ? formatCurrency(cost, 'PLN') : '-',
    },
    {
      title: t('inventory:fields.totalValue'),
      dataIndex: 'totalValuePLN',
      key: 'totalValuePLN',
      width: 140,
      render: (value: number) => value ? formatCurrency(value, 'PLN') : '-',
    }] : []),
    {
      title: t('inventory:fields.status'),
      key: 'status',
      width: 120,
      render: (record: InventoryWithDetails) => {
        const { text, color } = getStockStatus(record);
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: t('inventory:fields.lastMovement'),
      dataIndex: 'lastMovementAt',
      key: 'lastMovementAt',
      width: 120,
      render: (date: string) => date ? formatDate(date) : '-',
    },
  ];

  // Expanded row to show batches and movements
  const expandedRowRender = (record: InventoryWithDetails) => {
    const variantId = record.productVariantId;
    const variantBatches = batches[variantId] || [];
    const variantMovements = movements[variantId] || [];

    const batchColumns = [
      {
        title: t('inventory:batchFields.batchNumber'),
        dataIndex: 'batchNumber',
        key: 'batchNumber',
      },
      {
        title: t('inventory:batchFields.receiptDate'),
        dataIndex: 'receiptDate',
        key: 'receiptDate',
        render: (date: string) => formatDate(date),
      },
      {
        title: t('inventory:batchFields.quantityReceived'),
        dataIndex: 'quantityReceived',
        key: 'quantityReceived',
      },
      {
        title: t('inventory:batchFields.quantityRemaining'),
        dataIndex: 'quantityRemaining',
        key: 'quantityRemaining',
        render: (qty: number, batch: InventoryBatch) => (
          <Progress
            percent={Math.round((qty / batch.quantityReceived) * 100)}
            size="small"
            format={() => qty}
          />
        ),
      },
      ...(canViewPurchasePrices ? [{
        title: t('inventory:batchFields.unitCost'),
        dataIndex: 'unitCostPLN',
        key: 'unitCostPLN',
        render: (cost: number) => formatCurrency(cost, 'PLN'),
      }] : []),
      {
        title: t('inventory:batchFields.status'),
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
          <Tag color={status === 'ACTIVE' ? 'green' : status === 'DEPLETED' ? 'default' : 'red'}>
            {t(`inventory:batchStatus.${status.toLowerCase()}`)}
          </Tag>
        ),
      },
    ];

    const movementColumns = [
      {
        title: t('inventory:movementFields.date'),
        dataIndex: 'movementDate',
        key: 'movementDate',
        render: (date: string) => formatDate(date),
      },
      {
        title: t('inventory:movementFields.type'),
        dataIndex: 'movementType',
        key: 'movementType',
        render: (type: string) => getMovementTypeTag(type),
      },
      {
        title: t('inventory:movementFields.quantity'),
        dataIndex: 'quantity',
        key: 'quantity',
        render: (qty: number, mov: InventoryMovement) => (
          <Text type={mov.movementType.includes('IN') ? 'success' : 'danger'}>
            {mov.movementType.includes('IN') ? '+' : '-'}{Math.abs(qty)}
          </Text>
        ),
      },
      {
        title: t('inventory:movementFields.reference'),
        dataIndex: 'referenceNumber',
        key: 'referenceNumber',
        render: (ref: string) => ref || '-',
      },
      {
        title: t('inventory:movementFields.notes'),
        dataIndex: 'notes',
        key: 'notes',
        render: (notes: string) => notes || '-',
      },
    ];

    // Earmark columns for the per-batch "reserved for orders" sub-table.
    // Uses inventory:earmarks.* keys that were pre-provisioned in Ticket C.
    const earmarkColumns = [
      {
        title: t('inventory:earmarks.colOrder'),
        dataIndex: 'orderNumber',
        key: 'orderNumber',
      },
      {
        title: t('inventory:earmarks.colQty'),
        dataIndex: 'quantity',
        key: 'quantity',
      },
      {
        title: t('inventory:earmarks.colConsumed'),
        dataIndex: 'consumedQuantity',
        key: 'consumedQuantity',
      },
      {
        title: t('inventory:earmarks.colStatus'),
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => (
          <Tag color={status === 'ACTIVE' ? 'blue' : status === 'FULFILLED' ? 'green' : 'default'}>
            {t(`inventory:earmarks.status_${status}`)}
          </Tag>
        ),
      },
      {
        title: t('inventory:earmarks.colEarmarkedAt'),
        dataIndex: 'earmarkedAt',
        key: 'earmarkedAt',
        render: (date: string) => formatDate(date),
      },
    ];

    // Expanded sub-row for a single batch — shows active earmarks (orders that
    // reserved units from this batch). Loads on first expand; subsequent expands
    // use the cached result from batchEarmarks state.
    const batchExpandedRowRender = (batch: InventoryBatch) => {
      const earmarks = batchEarmarks[batch.id];
      // Filter to ACTIVE earmarks only — fulfilled/released are noise in this view
      const activeEarmarks = earmarks
        ? earmarks.filter((e: BatchEarmark) => e.status === 'ACTIVE')
        : undefined;

      return (
        <div style={{ padding: '8px 16px', background: '#f8fafc' }}>
          <div style={{ fontWeight: 600, marginBottom: 8, color: '#475569', fontSize: 12 }}>
            {t('inventory:earmarks.title')}
          </div>
          <Table
            columns={earmarkColumns}
            dataSource={activeEarmarks}
            loading={earmarks === undefined}
            pagination={false}
            rowKey="id"
            size="small"
            locale={{ emptyText: t('inventory:earmarks.noData') }}
          />
        </div>
      );
    };

    return (
      <div style={{ padding: '16px' }}>
        <Row gutter={24}>
          <Col xs={24} xl={12}>
            <Card title={t('inventory:sections.batches')} size="small">
              <Table
                columns={batchColumns}
                dataSource={variantBatches}
                pagination={false}
                rowKey="id"
                size="small"
                locale={{ emptyText: t('inventory:noBatches') }}
                scroll={{ x: 620 }}
                expandable={{
                  expandedRowRender: batchExpandedRowRender,
                  onExpand: (expanded: boolean, batch: InventoryBatch) => {
                    if (expanded) {
                      fetchEarmarksForBatch(batch.id);
                    }
                  },
                }}
              />
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card title={t('inventory:sections.recentMovements')} size="small">
              <Table
                columns={movementColumns}
                dataSource={variantMovements}
                pagination={false}
                rowKey="id"
                size="small"
                locale={{ emptyText: t('inventory:noMovements') }}
                scroll={{ x: 620 }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div className="inventory-dashboard-page">
      <div className="inventory-dashboard-page__header">
        <h1 style={{ margin: 0 }}>
          <DashboardOutlined style={{ marginRight: 8 }} />
          {t('inventory:title')}
        </h1>
        <Space>
          {canViewPurchasePrices && (
            <Button
              icon={<BarChartOutlined />}
              onClick={() => navigate('/app/inventory/reports')}
            >
              {t('inventory:actions.viewReports')}
            </Button>
          )}
        </Space>
      </div>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('inventory:summary.totalItems')}
              value={summary.totalItems}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        {canViewPurchasePrices && (
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title={t('inventory:summary.totalValue')}
                value={summary.totalValue}
                precision={2}
                suffix="PLN"
              />
            </Card>
          </Col>
        )}
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('inventory:summary.lowStock')}
              value={summary.lowStockCount}
              valueStyle={{ color: summary.lowStockCount > 0 ? '#faad14' : undefined }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('inventory:summary.outOfStock')}
              value={summary.outOfStockCount}
              valueStyle={{ color: summary.outOfStockCount > 0 ? '#ff4d4f' : undefined }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Low Stock Alert */}
      {summary.lowStockCount > 0 && (
        <Alert
          message={t('inventory:alerts.lowStockTitle')}
          description={t('inventory:alerts.lowStockDescription', { count: summary.lowStockCount })}
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          action={
            <Button
              size="small"
              onClick={() => setFilters({ ...filters, lowStock: true })}
            >
              {t('inventory:actions.showLowStock')}
            </Button>
          }
        />
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder={t('inventory:filters.search')}
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('inventory:filters.status')}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="ACTIVE">{t('inventory:status.inStock')}</Option>
              <Option value="LOW_STOCK">{t('inventory:status.lowStock')}</Option>
              <Option value="OUT_OF_STOCK">{t('inventory:status.outOfStock')}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button
              type={filters.lowStock ? 'primary' : 'default'}
              icon={<WarningOutlined />}
              onClick={() => setFilters({ ...filters, lowStock: !filters.lowStock })}
            >
              {t('inventory:filters.lowStockOnly')}
            </Button>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              onClick={() => setFilters({ search: '', status: null, lowStock: false })}
            >
              {t('common:clearFilters')}
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Inventory Table */}
      <Card>
        <Table
          dataSource={filteredInventory}
          columns={columns}
          loading={loading}
          rowKey="productVariantId"
          scroll={{ x: 920 }}
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            onExpand: handleExpand,
          }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) => t('common:pagination', { from: range[0], to: range[1], total }),
            pageSizeOptions: ['10', '25', '50', '100'],
          }}
        />
      </Card>
    </div>
  );
};

export default InventoryDashboard;
